import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Switch, Radio } from 'components/ds/Choice'
import { Input } from 'components/ds/Input'
import { Badge, Tag } from 'components/ds/Badge'
import Button from 'components/ds/Button'
import Icon from 'components/ds/Icon'
import { getDeclineEmailOptions } from 'actions/applicants'
import styles from './DeclineEmailSection.module.scss'

const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/
const MAX_RECIPIENTS = 20
// Send limits are higher than the saved template's (200 / 5,000) because {businessName} is already filled in here.
const SUBJECT_MAX = 500
const MESSAGE_MAX = 10000

const SOURCE_LABELS = {
    OWNER: 'Owner',
    BUSINESS: 'Business',
    CONTACT: 'Contact',
    BROKER: 'Broker',
    PORTAL_USER: 'Portal user',
}

const fmt = n => Number(n).toLocaleString('en-US')

// Subject, message and reply-to are filled from the client's saved defaults once the options load.
export const defaultDeclineEmail = () => ({
    send: false,
    recipients: [],
    replyTo: null,
    subject: '',
    message: '',
})

/** Field errors for the dialog; empty when the email can be sent (or isn't being sent). */
export const declineEmailErrors = (value) => {
    const errors = {}
    if (!value.send) return errors
    if (!value.recipients.length) errors.recipients = 'Add at least one recipient.'
    if (!value.subject.trim()) errors.subject = 'Enter a subject.'
    if (!value.message.trim()) errors.message = 'Enter a message.'
    return errors
}

/** The dialog footer line: what Confirm will do, or how many fields need fixing. */
export const declineEmailFooterNote = (value, submitted) => {
    const count = Object.keys(submitted ? declineEmailErrors(value) : {}).length
    if (count) return { text: `Fix ${count} ${count === 1 ? 'field' : 'fields'} to confirm, or turn off the email.`, error: true }
    if (!value.send) return { text: 'Declines the application. No email is sent.', error: false }
    const n = value.recipients.length
    return { text: `Declines the application and emails ${n} ${n === 1 ? 'recipient' : 'recipients'}.`, error: false }
}

/**
 * `onChange` must accept an updater function (a useState setter does).
 * Optional applicant email inside the decline dialog (flag APPLICATION_DECLINE_EMAIL). Follows the "Decline email
 * board" design: suggestions from the application, free-typed addresses, and the message collapsed by default.
 */
const DeclineEmailSection = ({ applicationUUID, value, onChange, submitted }) => {
    const dispatch = useDispatch()
    const [options, setOptions] = useState(null)
    const [draft, setDraft] = useState('')
    const [draftError, setDraftError] = useState('')
    const [collapsed, setCollapsed] = useState(true)

    useEffect(() => {
        if (!value.send || options) return
        dispatch(getDeclineEmailOptions(applicationUUID))
            .then(res => {
                const loaded = { suggestions: [], ...res }
                setOptions(loaded)
                // Pre-fill from the saved defaults (the backend already filled in the business name), merged into
                // the latest state so anything the user did while this loaded wins.
                onChange(current => ({
                    ...current,
                    subject: current.subject || loaded.subject || '',
                    message: current.message || loaded.message || '',
                    replyTo: current.replyTo || loaded.replyTo || 'ME',
                }))
            })
            .catch(() => {
                setOptions({ suggestions: [] })
                // Without a reply choice the backend would reject the whole decline.
                onChange(current => ({ ...current, replyTo: current.replyTo || 'ME' }))
            })
    }, [value.send, applicationUUID, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

    const errors = submitted ? declineEmailErrors(value) : {}

    // Open the message editor when Confirm finds a problem in it.
    useEffect(() => {
        if (errors.subject || errors.message) setCollapsed(false)
    }, [errors.subject, errors.message])

    const update = patch => onChange({ ...value, ...patch })
    const has = email => value.recipients.some(r => r.toLowerCase() === email.toLowerCase())
    const full = value.recipients.length >= MAX_RECIPIENTS

    // Adds every valid address in the text; invalid ones stay in the input with an error.
    const addRecipients = text => {
        const parts = text.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean)
        const next = [...value.recipients]
        const bad = []
        let over = false
        parts.forEach(email => {
            if (!EMAIL_PATTERN.test(email)) { bad.push(email); return }
            if (next.some(r => r.toLowerCase() === email.toLowerCase())) return
            if (next.length >= MAX_RECIPIENTS) { over = true; return }
            next.push(email)
        })
        update({ recipients: next })
        setDraft(bad.join(', '))
        setDraftError(bad.length === 1 ? `“${bad[0]}” isn't a valid email address.`
            : bad.length > 1 ? `${bad.length} addresses aren't valid: ${bad.join(', ')}`
                : over ? `You can add up to ${MAX_RECIPIENTS} recipients.` : '')
    }

    const onDraftKeyDown = e => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            if (draft.trim()) addRecipients(draft)
        } else if (e.key === 'Backspace' && !draft && value.recipients.length) {
            update({ recipients: value.recipients.slice(0, -1) })
        }
    }

    const onDraftPaste = e => {
        const text = e.clipboardData.getData('text')
        if (/[,;\n]/.test(text)) {
            e.preventDefault()
            addRecipients(`${draft},${text}`)
        }
    }

    const suggestions = options?.suggestions || []
    const allAdded = suggestions.length > 0 && suggestions.every(s => has(s.email))
    const edited = options && (value.subject !== (options.subject || '') || value.message !== (options.message || ''))
    // The summary skips the greeting paragraph when there is one.
    const paragraphs = value.message.split(/\n\s*\n/)
    const excerpt = (paragraphs[1] || value.message).replace(/\s+/g, ' ').trim()
    const recipientError = draftError || errors.recipients

    const replyOptions = [
        { value: 'ME', label: 'My email', detail: options?.myEmail },
        { value: 'COMPANY', label: 'Company email', detail: options?.companyEmail || 'Not set', disabled: !options?.companyEmail },
        { value: 'NONE', label: "Don't allow replies" },
    ]

    return (
        <div className={styles.section}>
            <div className={styles.switchRow}>
                <Switch
                    label="Email the applicant about this decision"
                    checked={value.send}
                    onChange={e => update({ send: e.target.checked })}
                />
                {options?.fromAddress && (
                    <span className={styles.switchCaption}>
                        Sent from {options.fromAddress} on behalf of {options.clientName}.
                    </span>
                )}
            </div>

            {value.send && (
                <>
                    <div className={styles.group}>
                        <div className={styles.labelRow}>
                            <label className={styles.label} htmlFor="decline-email-recipients">
                                Recipients<span className={styles.required}>&nbsp;*</span>
                            </label>
                            <span className={styles.count}>{value.recipients.length} of {MAX_RECIPIENTS}</span>
                        </div>
                        <Input
                            id="decline-email-recipients"
                            placeholder="Type an email and press Enter. Separate several with commas."
                            value={draft}
                            disabled={full}
                            onChange={e => { setDraft(e.target.value); setDraftError('') }}
                            onKeyDown={onDraftKeyDown}
                            onPaste={onDraftPaste}
                            onBlur={() => draft.trim() && addRecipients(draft)}
                            error={recipientError || undefined}
                        />
                        {value.recipients.length > 0 && (
                            <div className={styles.tags}>
                                {value.recipients.map(email => (
                                    <Tag
                                        key={email}
                                        removeLabel={`Remove ${email}`}
                                        onRemove={() => update({ recipients: value.recipients.filter(r => r !== email) })}
                                    >
                                        {email}
                                    </Tag>
                                ))}
                            </div>
                        )}
                        {suggestions.length > 0 && (
                            <div className={styles.suggested}>
                                <div className={styles.labelRow}>
                                    <span className={styles.caption}>Suggested from this application</span>
                                    <Button variant="ghost" size="small" disabled={allAdded}
                                            onClick={() => addRecipients(suggestions.map(s => s.email).join(','))}>
                                        Add all
                                    </Button>
                                </div>
                                <div className={styles.chips}>
                                    {suggestions.map(s => {
                                        const added = has(s.email)
                                        return (
                                            <button
                                                type="button"
                                                key={s.email}
                                                className={styles.chip}
                                                disabled={added}
                                                title={s.email}
                                                aria-label={`${added ? 'Added' : 'Add'} ${s.name || ''} ${s.email}`.trim()}
                                                onClick={() => addRecipients(s.email)}
                                            >
                                                <Icon name={added ? 'check' : 'plus'} size={16} />
                                                <Badge dot={false} className={styles.chipBadge}>{SOURCE_LABELS[s.source] || s.source}</Badge>
                                                {s.name && <span className={styles.chipName}>{s.name}</span>}
                                                <span className={styles.chipEmail}>{s.email}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {collapsed ? (
                        <div className={styles.group}>
                            <span className={styles.label}>Message</span>
                            <div className={styles.summary}>
                                <div className={styles.summaryText}>
                                    <div className={styles.summaryTitle}>
                                        <span className={styles.summarySubject}>{value.subject || 'No subject'}</span>
                                        {edited && <Badge tone="review">Edited</Badge>}
                                    </div>
                                    <span className={styles.summaryExcerpt}>{excerpt || 'No message'}</span>
                                </div>
                                <Button variant="secondary" size="small" onClick={() => setCollapsed(false)}>Edit message</Button>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.group}>
                            <div className={styles.labelRow}>
                                <span className={styles.label}>Message</span>
                                <div className={styles.labelActions}>
                                    {edited && (
                                        <Button variant="ghost" size="small"
                                                onClick={() => update({ subject: options.subject || '', message: options.message || '' })}>
                                            Reset to defaults
                                        </Button>
                                    )}
                                    <Button variant="ghost" size="small" onClick={() => setCollapsed(true)}>Collapse</Button>
                                </div>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.subLabel} htmlFor="decline-email-subject-send">Subject</label>
                                <Input
                                    id="decline-email-subject-send"
                                    maxLength={SUBJECT_MAX}
                                    value={value.subject}
                                    onChange={e => update({ subject: e.target.value })}
                                    error={errors.subject}
                                />
                                <span className={styles.countRight}>{fmt(value.subject.length)} / {fmt(SUBJECT_MAX)}</span>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.subLabel} htmlFor="decline-email-message-send">Message</label>
                                <Input
                                    id="decline-email-message-send"
                                    multiline
                                    rows={8}
                                    controlClassName={styles.message}
                                    maxLength={MESSAGE_MAX}
                                    value={value.message}
                                    onChange={e => update({ message: e.target.value })}
                                    error={errors.message}
                                />
                                <span className={styles.countRight}>{fmt(value.message.length)} / {fmt(MESSAGE_MAX)}</span>
                            </div>
                        </div>
                    )}

                    <fieldset className={styles.replies}>
                        <legend className={styles.label}>Replies go to</legend>
                        {replyOptions.map(o => (
                            <div key={o.value} className={styles.replyOption}>
                                <Radio
                                    name="declineEmailReplyTo"
                                    label={o.label}
                                    checked={value.replyTo === o.value}
                                    disabled={o.disabled}
                                    onChange={() => update({ replyTo: o.value })}
                                />
                                {o.detail && <span className={o.disabled ? styles.detailDisabled : styles.caption}>{o.detail}</span>}
                            </div>
                        ))}
                    </fieldset>
                </>
            )}
        </div>
    )
}

export default DeclineEmailSection
